// Supabase data-access layer for the Offers & Applications domain.
// Components and stores must never call Supabase directly for this domain.

import { supabase } from '@/integrations/supabase/client';
import type {
  Offer,
  OfferPricing,
  Application,
  ApplicationStatus,
  OfferType,
  OfferStatus,
} from '@/types/offer';
import type { AgeCategory } from '@/constants/offerOptions';

export const OFFERS_PAGE_SIZE = 20;

type OfferRow = {
  id: string;
  created_by: string;
  title: string;
  description: string;
  location: string;
  date: string;
  type: string;
  level: string | null;
  positions_wanted: string[] | null;
  time: string | null;
  start_time: string | null;
  end_time: string | null;
  organizer: string | null;
  places: number | null;
  price: string | null;
  pricing: unknown;
  age_category: string | null;
  cover_image: string | null;
  status: string;
  created_at: string;
};

function mapOffer(row: OfferRow): Offer {
  return {
    id: row.id,
    createdById: row.created_by,
    title: row.title,
    description: row.description ?? '',
    location: row.location ?? '',
    date: row.date ?? '',
    type: row.type as OfferType,
    level: row.level ?? undefined,
    positionsWanted: row.positions_wanted?.length ? row.positions_wanted : undefined,
    time: row.time ?? undefined,
    startTime: row.start_time ?? undefined,
    endTime: row.end_time ?? undefined,
    organizer: row.organizer ?? undefined,
    places: row.places ?? undefined,
    price: row.price ?? undefined,
    pricing: (row.pricing as OfferPricing | null) ?? undefined,
    ageCategory: (row.age_category as AgeCategory | null) ?? undefined,
    coverImage: row.cover_image ?? undefined,
    status: (row.status as OfferStatus) ?? 'active',
    registrations: [],
    createdAt: row.created_at,
  };
}

type OfferInput = Partial<Omit<Offer, 'id' | 'createdById' | 'createdAt' | 'registrations'>>;

function toRow(data: OfferInput): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (data.title !== undefined) row.title = data.title;
  if (data.description !== undefined) row.description = data.description ?? '';
  if (data.location !== undefined) row.location = data.location ?? '';
  if (data.date !== undefined) row.date = data.date ?? '';
  if (data.type !== undefined) row.type = data.type;
  if (data.level !== undefined) row.level = data.level ?? null;
  if (data.positionsWanted !== undefined) row.positions_wanted = data.positionsWanted ?? [];
  if (data.time !== undefined) row.time = data.time ?? null;
  if (data.startTime !== undefined) row.start_time = data.startTime ?? null;
  if (data.endTime !== undefined) row.end_time = data.endTime ?? null;
  if (data.organizer !== undefined) row.organizer = data.organizer ?? null;
  if (data.places !== undefined) row.places = data.places ?? null;
  if (data.price !== undefined) row.price = data.price ?? null;
  if (data.pricing !== undefined) row.pricing = data.pricing ?? null;
  if (data.ageCategory !== undefined) row.age_category = data.ageCategory ?? null;
  if (data.coverImage !== undefined) row.cover_image = data.coverImage ?? null;
  if (data.status !== undefined) row.status = data.status;
  return row;
}

/** Paginated offer list, newest first. */
export async function fetchOffersPage(
  page: number,
  pageSize = OFFERS_PAGE_SIZE,
): Promise<{ offers: Offer[]; hasMore: boolean }> {
  const from = page * pageSize;
  const { data, error } = await supabase
    .from('offers')
    .select('*')
    .order('created_at', { ascending: false })
    .range(from, from + pageSize - 1);
  if (error) throw error;
  const rows = (data ?? []) as unknown as OfferRow[];
  const offers = rows.map(mapOffer);
  await attachRegistrations(offers);
  return { offers, hasMore: rows.length === pageSize };
}

export async function fetchOfferById(id: string): Promise<Offer | null> {
  const { data, error } = await supabase.from('offers').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const offer = mapOffer(data as unknown as OfferRow);
  await attachRegistrations([offer]);
  return offer;
}

/** Fill in tournament registrations for the given offers. */
export async function attachRegistrations(offers: Offer[]): Promise<void> {
  if (offers.length === 0) return;
  const { data, error } = await supabase
    .from('offer_registrations')
    .select('offer_id, user_id, created_at')
    .in('offer_id', offers.map((o) => o.id));
  if (error) {
    console.warn('[offers] registrations fetch failed', error);
    return;
  }
  const byOffer = new Map<string, { userId: string; at: string }[]>();
  for (const row of data ?? []) {
    const list = byOffer.get(row.offer_id) ?? [];
    list.push({ userId: row.user_id, at: row.created_at });
    byOffer.set(row.offer_id, list);
  }
  offers.forEach((offer) => {
    offer.registrations = byOffer.get(offer.id) ?? [];
  });
}

export async function insertOffer(userId: string, data: OfferInput): Promise<Offer> {
  const { data: row, error } = await supabase
    .from('offers')
    .insert({ ...toRow(data), created_by: userId } as never)
    .select('*')
    .single();
  if (error) throw error;
  return mapOffer(row as unknown as OfferRow);
}

export async function patchOffer(offerId: string, data: OfferInput): Promise<void> {
  const { error } = await supabase.from('offers').update(toRow(data) as never).eq('id', offerId);
  if (error) throw error;
}

export async function removeOffer(offerId: string): Promise<void> {
  const { error } = await supabase.from('offers').delete().eq('id', offerId);
  if (error) throw error;
}

// ---------------------------------------------------------------- applications

type ApplicationRow = {
  id: string;
  offer_id: string;
  applicant_id: string;
  status: string;
  message: string | null;
  document_url: string | null;
  created_at: string;
};

function mapApplication(row: ApplicationRow): Application {
  return {
    id: row.id,
    offerId: row.offer_id,
    applicantId: row.applicant_id,
    status: row.status as ApplicationStatus,
    message: row.message ?? undefined,
    documentUrl: row.document_url ?? undefined,
    createdAt: row.created_at,
  };
}

/** RLS already restricts this to: my applications + applications on my offers. */
export async function fetchVisibleApplications(): Promise<Application[]> {
  const { data, error } = await supabase
    .from('applications')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as ApplicationRow[]).map(mapApplication);
}

export async function insertApplication(
  offerId: string,
  applicantId: string,
  extra?: { message?: string; documentUrl?: string },
): Promise<Application> {
  const { data, error } = await supabase
    .from('applications')
    .insert({
      offer_id: offerId,
      applicant_id: applicantId,
      status: 'pending',
      message: extra?.message ?? null,
      document_url: extra?.documentUrl ?? null,
    } as never)
    .select('*')
    .single();
  if (error) throw error;
  return mapApplication(data as unknown as ApplicationRow);
}

export async function patchApplicationStatus(
  applicationId: string,
  status: ApplicationStatus,
): Promise<void> {
  const { error } = await supabase
    .from('applications')
    .update({ status } as never)
    .eq('id', applicationId);
  if (error) throw error;
}

export async function removeApplication(applicationId: string): Promise<void> {
  const { error } = await supabase.from('applications').delete().eq('id', applicationId);
  if (error) throw error;
}

// ---------------------------------------------------------------- saved offers

export async function fetchSavedOfferIds(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('saved_offers')
    .select('offer_id')
    .eq('user_id', userId);
  if (error) throw error;
  return (data ?? []).map((row) => row.offer_id as string);
}

export async function saveOffer(offerId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('saved_offers')
    .insert({ offer_id: offerId, user_id: userId } as never);
  if (error) throw error;
}

export async function unsaveOffer(offerId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('saved_offers')
    .delete()
    .eq('offer_id', offerId)
    .eq('user_id', userId);
  if (error) throw error;
}

// -------------------------------------------------------------- registrations

export async function insertRegistration(offerId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('offer_registrations')
    .insert({ offer_id: offerId, user_id: userId } as never);
  if (error) throw error;
}

export async function removeRegistration(offerId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('offer_registrations')
    .delete()
    .eq('offer_id', offerId)
    .eq('user_id', userId);
  if (error) throw error;
}
